import { useEffect, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  DAYS_OF_WEEK,
  SLOT_TYPES,
  type AvailabilityRuleUI,
  type DayOfWeek,
  type SlotType,
  type AppointmentProviderRow,
  type AppointmentServiceWithCategoryRow,
} from '@helios/data-access';

// ─── Shared styles (mirror ProviderDrawer / BookAppointmentDrawer) ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  lineHeight: 'var(--line-height-sm)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  display: 'block',
  marginBottom: 'var(--sp-2)',
};

const inputStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

const selectStyle: CSSProperties = { ...inputStyle, cursor: 'pointer', appearance: 'auto' };

const DAY_LABELS: Record<DayOfWeek, string> = {
  SUNDAY: 'Sun',
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
};

const SLOT_TYPE_LABELS: Record<SlotType, string> = {
  REGULAR: 'Regular',
  SAME_DAY: 'Same-day',
  WALK_IN: 'Walk-in',
  FOLLOW_UP: 'Follow-up',
  PROCEDURE: 'Procedure',
  EMERGENCY: 'Emergency',
};

// ─── Day-of-week picker ───

function DaysOfWeekPicker({
  value,
  onChange,
}: {
  value: DayOfWeek[];
  onChange: (next: DayOfWeek[]) => void;
}) {
  const toggle = (d: DayOfWeek) => {
    onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d]);
  };
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
      {DAYS_OF_WEEK.map((d) => {
        const selected = value.includes(d);
        return (
          <button
            key={d}
            type="button"
            onClick={() => toggle(d)}
            style={{
              minWidth: 56,
              height: 36,
              padding: '0 var(--sp-3)',
              borderRadius: 'var(--radius-full)',
              background: selected ? 'var(--color-primary)' : 'var(--input-bg)',
              color: selected ? '#fff' : 'var(--text)',
              border: selected ? '1px solid var(--color-primary)' : '1px solid var(--border-input)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            {DAY_LABELS[d]}
          </button>
        );
      })}
    </div>
  );
}

// ─── Toggle (compact) ───

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Drawer ───

export interface AvailabilityRuleDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  rule: AvailabilityRuleUI | null;
  providers: AppointmentProviderRow[];
  services: AppointmentServiceWithCategoryRow[];
  onSave: (rule: Omit<AvailabilityRuleUI, 'id'>) => void | Promise<void>;
}

const EMPTY_FORM: Omit<AvailabilityRuleUI, 'id'> = {
  name: '',
  providerId: '',
  serviceId: '',
  daysOfWeek: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
  startTime: '08:00',
  endTime: '12:00',
  slotDurationMinutes: 30,
  capacityPerSlot: 1,
  slotType: 'REGULAR',
  effectiveStartDate: null,
  effectiveEndDate: null,
  autoGenerate: true,
  generateDaysAhead: 60,
  notes: '',
  isActive: true,
};

export function AvailabilityRuleDrawer({
  open,
  onOpenChange,
  brandTokens,
  rule,
  providers,
  services,
  onSave,
}: AvailabilityRuleDrawerProps) {
  const [form, setForm] = useState<Omit<AvailabilityRuleUI, 'id'>>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      const { id: _id, ...rest } = rule;
      setForm(rest);
    } else {
      // When creating, seed slot duration from the picked service when chosen.
      setForm(EMPTY_FORM);
    }
  }, [open, rule]);

  const update = <K extends keyof Omit<AvailabilityRuleUI, 'id'>>(
    key: K,
    val: Omit<AvailabilityRuleUI, 'id'>[K],
  ) => setForm((f) => ({ ...f, [key]: val }));

  const handleServiceChange = (id: string) => {
    const svc = services.find((s) => s.id === id);
    setForm((f) => ({
      ...f,
      serviceId: id,
      // Only adopt service default duration when creating a new rule.
      slotDurationMinutes: rule
        ? f.slotDurationMinutes
        : svc?.typical_duration_mins || f.slotDurationMinutes,
    }));
  };

  const canSave =
    form.name.trim().length > 0 &&
    !!form.providerId &&
    !!form.serviceId &&
    form.daysOfWeek.length > 0 &&
    !!form.startTime &&
    !!form.endTime &&
    form.startTime < form.endTime &&
    form.slotDurationMinutes > 0 &&
    form.capacityPerSlot >= 1 &&
    (!form.autoGenerate || form.generateDaysAhead >= 1);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const isEdit = !!rule;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit Availability' : 'Add Availability'}
      description="Provider availability that generates bookable appointment slots."
      width={520}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Availability' : 'Add Availability'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        <div>
          <label style={labelStyle}>
            Name<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <input
            type="text"
            placeholder="Dr. Kris Morning Clinic"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>
            Provider<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <select
            value={form.providerId}
            onChange={(e) => update('providerId', e.target.value)}
            style={selectStyle}
          >
            <option value="">Select provider...</option>
            {providers
              .filter((p) => p.active == 1)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name || p.full_name}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>
            Service<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <select
            value={form.serviceId}
            onChange={(e) => handleServiceChange(e.target.value)}
            style={selectStyle}
          >
            <option value="">Select service...</option>
            {services
              .filter((s) => s.active == 1)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>
            Days of Week<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <DaysOfWeekPicker value={form.daysOfWeek} onChange={(d) => update('daysOfWeek', d)} />
        </div>

        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>
              Start Time<span style={{ color: 'var(--color-danger)' }}> *</span>
            </label>
            <input
              type="time"
              value={form.startTime}
              onChange={(e) => update('startTime', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>
              End Time<span style={{ color: 'var(--color-danger)' }}> *</span>
            </label>
            <input
              type="time"
              value={form.endTime}
              onChange={(e) => update('endTime', e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>
              Slot Duration (mins)<span style={{ color: 'var(--color-danger)' }}> *</span>
            </label>
            <input
              type="number"
              min={5}
              step={5}
              value={form.slotDurationMinutes}
              onChange={(e) => update('slotDurationMinutes', Number(e.target.value) || 0)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>
              Max Patients per Slot<span style={{ color: 'var(--color-danger)' }}> *</span>
            </label>
            <input
              type="number"
              min={1}
              value={form.capacityPerSlot}
              onChange={(e) => update('capacityPerSlot', Number(e.target.value) || 1)}
              style={inputStyle}
            />
          </div>
        </div>

        <div>
          <label style={labelStyle}>
            Slot Type<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <select
            value={form.slotType}
            onChange={(e) => update('slotType', e.target.value as SlotType)}
            style={selectStyle}
          >
            {SLOT_TYPES.map((t) => (
              <option key={t} value={t}>
                {SLOT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Effective Start</label>
            <input
              type="date"
              value={form.effectiveStartDate ?? ''}
              onChange={(e) => update('effectiveStartDate', e.target.value || null)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Effective End</label>
            <input
              type="date"
              value={form.effectiveEndDate ?? ''}
              onChange={(e) => update('effectiveEndDate', e.target.value || null)}
              style={inputStyle}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface-2)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Auto-generate Slots
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Generate slots for the future window when this rule is saved.
            </div>
          </div>
          <Toggle checked={form.autoGenerate} onChange={(v) => update('autoGenerate', v)} />
        </div>

        <div>
          <label style={labelStyle}>Generate Days Ahead</label>
          <input
            type="number"
            min={1}
            value={form.generateDaysAhead}
            onChange={(e) => update('generateDaysAhead', Number(e.target.value) || 1)}
            disabled={!form.autoGenerate}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Notes</label>
          <textarea
            placeholder="Internal notes..."
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
            rows={3}
            style={{
              ...inputStyle,
              height: 'auto',
              padding: 'var(--sp-3) var(--input-padding-x)',
              resize: 'vertical',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Active
          </div>
          <Toggle checked={form.isActive} onChange={(v) => update('isActive', v)} />
        </div>
      </div>
    </Drawer>
  );
}
